import { useState } from 'react';
import type { VideoStatus } from '@ddp/shared';

import { useVideoCover } from './use-video-cover.js';

interface VideoThumbnailProps {
  videoId: string;
  status: VideoStatus;
  /**
   * URL pré-signée de la couverture, quand l'appelant la connaît déjà (liste `/videos`). Fournie, on
   * l'utilise telle quelle (aucune requête). Omise, le composant la récupère lui-même via `/playback`.
   */
  coverUrl?: string | null;
  /** Classe du cadre (dimensions, coins, fond de repli). */
  className: string;
  /** Classe de l'élément média qui remplit le cadre. */
  mediaClassName?: string;
  /** Classe du repli affiché quand aucune image n'est disponible. */
  emptyClassName?: string;
}

/**
 * Aperçu d'une vidéo : l'**image de couverture** (`cover.jpg`) extraite par le worker à 10 % de la
 * durée (jamais une première image noire), servie comme un simple JPEG. Peindre la source elle-même
 * (ancien `<video src=…#t=0.1>`) échouait en silence sur un MP4 non « faststart » ou un HEVC — le cas
 * courant des sources drone sur Safari iOS : vignette grise alors que la vidéo est lisible en HLS. Un
 * `<img>` s'affiche quel que soit le codec. Repli neutre tant que la couverture n'existe pas.
 *
 * Composant unique partagé (cartes de la page Vidéos, lignes de l'Overview) : une seule logique de
 * source et de repli à maintenir.
 */
export function VideoThumbnail({
  videoId,
  status,
  coverUrl: coverUrlProp,
  className,
  mediaClassName,
  emptyClassName,
}: VideoThumbnailProps) {
  // URL fournie par la liste : on s'en sert telle quelle. Sinon seulement, on la récupère.
  const supplied = coverUrlProp !== undefined;
  const fetched = useVideoCover(videoId, status, !supplied);
  const coverUrl = supplied ? coverUrlProp : fetched;

  const [mediaError, setMediaError] = useState(false);
  const showMedia = Boolean(coverUrl) && !mediaError;

  return (
    <div className={className}>
      {showMedia ? (
        <img
          className={mediaClassName}
          src={coverUrl ?? undefined}
          alt=""
          loading="lazy"
          decoding="async"
          onError={() => setMediaError(true)}
        />
      ) : (
        <span className={emptyClassName} aria-hidden="true" />
      )}
    </div>
  );
}
